import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';

function App() {
  const [file, setFile] = useState(null);
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [selectedDataColumn, setSelectedDataColumn] = useState('');
  const [selectedNameColumns, setSelectedNameColumns] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [chunkSize, setChunkSize] = useState(1000);
  const [manualMode, setManualMode] = useState(false);
  const [manualColumns, setManualColumns] = useState('');
  const [isLargeFile, setIsLargeFile] = useState(false);
  const [fileSizeInfo, setFileSizeInfo] = useState('');
  const [tableStartColIndex, setTableStartColIndex] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;
    
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ];
    
    if (!validTypes.includes(selectedFile.type) && !selectedFile.name.match(/\.(xlsx|xls|csv)$/i)) {
      setError('Please select a valid Excel file (.xlsx, .xls) or CSV file.');
      return;
    }

    setFile(selectedFile);
    setError('');
    
    // Check file size and choose parsing method
    const fileSizeMB = selectedFile.size / (1024 * 1024);
    const fileSizeKB = selectedFile.size / 1024;
    const isLarge = fileSizeMB > 10;
    
    setIsLargeFile(isLarge);
    setFileSizeInfo(`File size: ${fileSizeKB.toFixed(1)} KB`);
    
    console.log(`📊 File size: ${fileSizeKB.toFixed(1)} KB (${fileSizeMB.toFixed(2)} MB)`);
    console.log(`📊 Is large file? ${isLarge}`);
    
    if (isLarge) {
      console.log(`📊 Large file detected (${fileSizeMB.toFixed(1)} MB) - using memory-efficient mode`);
      parseExcelFileLarge(selectedFile);
    } else {
      console.log(`📊 Small file detected (${fileSizeMB.toFixed(1)} MB) - using full-featured mode`);
      parseExcelFileSmall(selectedFile);
    }
  };

  const parseExcelFileSmall = (file) => {
    setIsProcessing(true);
    setProgress(25);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array', dense: true });
        
        // Get the first worksheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to JSON with full data for small files
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        if (jsonData.length === 0) {
          throw new Error('The Excel file appears to be empty.');
        }

        // Extract headers and data
        const headers = jsonData[0];
        const rows = jsonData.slice(1);
        
        // Convert to object format
        const formattedData = rows.map((row) => {
          const obj = {};
          headers.forEach((header, index) => {
            obj[header] = row[index] || '';
          });
          return obj;
        });

        setColumns(headers);
        setData(formattedData);
        setProgress(100);
        setIsProcessing(false);
        
        console.log(`✅ Small file loaded: ${headers.length} columns, ${formattedData.length} rows`);
        
      } catch (err) {
        console.error('Error in small file parsing:', err);
        
        // For small files, if standard parsing fails, try the large file method as fallback
        console.log('📊 Small file standard parsing failed, trying memory-optimized parsing...');
        setIsLargeFile(true); // Switch to large file mode
        parseExcelFileLarge(file);
      }
    };
    
    reader.onerror = () => {
      setError('Error reading the file.');
      setIsProcessing(false);
      setProgress(0);
    };
    
    reader.readAsArrayBuffer(file);
  };

  const parseExcelFileLarge = (file) => {
    setIsProcessing(true);
    setProgress(25);
    
    // For very large files, read only a small portion first
    const maxSampleSize = Math.min(file.size, 50 * 1024); // Only read first 50KB
    const slice = file.slice(0, maxSampleSize);
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        console.log(`📊 Reading sample (${maxSampleSize} bytes of ${file.size} bytes)...`);
        
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { 
          type: 'array',
          cellDates: false,
          cellNF: false,
          cellHTML: false,
          sheetRows: 1,  // Only read header row
          bookVBA: false,
          bookSheets: false,
          bookProps: false
        });
        
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        if (!worksheet || !worksheet['!ref']) {
          // If sample parsing fails, try manual header detection
          throw new Error('SAMPLE_FAILED');
        }
        
        const range = XLSX.utils.decode_range(worksheet['!ref']);
        
        // Extract headers from first row only
        const headers = [];
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const headerCell = worksheet[XLSX.utils.encode_cell({ c: C, r: 0 })];
          if (headerCell && headerCell.v) {
            headers.push(headerCell.v.toString());
          }
        }
        
        if (headers.length === 0) {
          throw new Error('SAMPLE_FAILED');
        }
        
        // Create a placeholder sample row
        const sampleRow = {};
        headers.forEach(header => {
          sampleRow[header] = `Sample data from ${header}`;
        });
        
        setColumns(headers);
        setData([sampleRow]);
        setProgress(100);
        setIsProcessing(false);
        
        console.log(`✅ Headers extracted: ${headers.length} columns from sample`);
        
        // Force cleanup
        if (window.gc) {
          setTimeout(() => window.gc(), 100);
        }
        
      } catch (err) {
        console.warn('Sample parsing failed, using fallback method:', err.message);

        // Second-chance: parse headers directly from worksheet XML inside the XLSX (without loading full data)
        try {
          const zip = new JSZip();
          const zipData = await zip.loadAsync(file);

          // Try Table headers first (Excel structured table headers)
          const tableFiles = Object.keys(zipData.files).filter(name => name.startsWith('xl/tables/') && name.endsWith('.xml'));
          if (tableFiles.length > 0) {
            const tableXml = await zipData.files[tableFiles[0]].async('text');
            const tableDoc = new DOMParser().parseFromString(tableXml, 'application/xml');
            const tableEl = queryTag(tableDoc, 'table')[0];
            if (tableEl) {
              const ref = tableEl.getAttribute('ref') || tableEl.getAttribute('displayName');
              // ref like "B3:G100" → start column from the left cell
              const rangeMatch = /(\w+)(\d+):(\w+)(\d+)/.exec(ref || 'A1');
              const startColLetters = rangeMatch ? rangeMatch[1] : 'A';
              const lettersToIndex = (letters) => {
                const u = letters.toUpperCase();
                let idx = 0;
                for (let i = 0; i < u.length; i++) idx = idx * 26 + (u.charCodeAt(i) - 64);
                return idx - 1;
              };
              const startIndex = lettersToIndex(startColLetters);

              const columnEls = queryTag(tableDoc, 'tableColumn');
              const headers = Array.from(columnEls).map((el, i) => el.getAttribute('name') || `Column${i + 1}`);

              if (headers.length > 0) {
                const sampleRow = {};
                headers.forEach(h => { sampleRow[h] = `Sample data from ${h}`; });

                setColumns(headers);
                setData([sampleRow]);
                setTableStartColIndex(startIndex);
                setProgress(100);
                setIsProcessing(false);
                setIsLargeFile(true);
                setError('');

                console.log(`✅ Headers extracted from Excel Table: ${headers.length} columns, startCol=${startIndex}`);
                return; // success via table headers
              }
            }
          }

          // If no table headers, attempt worksheet header cells
          // Read shared strings (if present)
          let sharedStrings = [];
          if (zipData.files['xl/sharedStrings.xml']) {
            sharedStrings = await loadSharedStrings(zipData);
          }

          // Find first worksheet and read its XML
          const bestSheet = await selectBestWorksheet(zipData);
          if (!bestSheet) throw new Error('No worksheet data found');

          const xmlParser = new DOMParser();
          const doc = bestSheet.doc || xmlParser.parseFromString(bestSheet.xml, 'text/xml');
          const rows = queryTag(doc, 'row');
          if (rows.length === 0 && bestSheet.xml && bestSheet.xml.includes('<row')) {
            console.warn('DOMParser returned 0 rows but <row> exists in XML; continuing with 0-row guard disabled');
          }

          const headerRow = rows[0];
          const headerCells = queryTag(headerRow, 'c');

          // Local helpers (avoid relying on hoisting)
          const getVal = (cell) => {
            const valueElement = cell.getElementsByTagName('v')[0];
            if (!valueElement) return '';
            const cellType = cell.getAttribute('t');
            const value = valueElement.textContent;
            if (cellType === 's') {
              const index = parseInt(value, 10);
              return sharedStrings[index] || '';
            }
            return value;
          };
          const colIndexFromRef = (ref) => {
            if (!ref) return 0;
            const match = /^[A-Za-z]+/.exec(ref);
            const letters = match ? match[0].toUpperCase() : 'A';
            let index = 0;
            for (let i = 0; i < letters.length; i++) {
              index = index * 26 + (letters.charCodeAt(i) - 64);
            }
            return index - 1;
          };

          const headers = [];
          for (let i = 0; i < headerCells.length; i++) {
            const cell = headerCells[i];
            const val = getVal(cell);
            const colIndex = colIndexFromRef(cell.getAttribute('r'));
            headers[colIndex] = val || `Column${colIndex + 1}`;
          }
          for (let i = 0; i < headers.length; i++) {
            if (!headers[i]) headers[i] = `Column${i + 1}`;
          }

          const sampleRow = {};
          headers.forEach(h => {
            sampleRow[h] = `Sample data from ${h}`;
          });

          setColumns(headers);
          setData([sampleRow]);
          setTableStartColIndex(null);
          setProgress(100);
          setIsProcessing(false);
          setIsLargeFile(true);
          setError('');

          console.log(`✅ Headers extracted via XML fallback: ${headers.length} columns`);
          return; // stop here; we recovered headers successfully
        } catch (secondaryErr) {
          console.warn('XML fallback failed:', secondaryErr.message);
        }

        // Final fallback: Create generic columns and let user proceed
        const fallbackHeaders = [];
        for (let i = 1; i <= 20; i++) { // Assume up to 20 columns
          fallbackHeaders.push(`Column${i}`);
        }

        const fallbackRow = {};
        fallbackHeaders.forEach(header => {
          fallbackRow[header] = 'Data will be determined during processing';
        });

        setColumns(fallbackHeaders);
        setData([fallbackRow]);
        setTableStartColIndex(null);
        setProgress(100);
        setIsProcessing(false);

        console.log('📋 Using fallback mode with generic columns');

        // Show warning to user
        setError('File too large for structure detection. Using generic column names. You may need to manually identify your data and name columns during extraction.');

        if (window.gc) {
          setTimeout(() => window.gc(), 100);
        }
      }
    };
    
    reader.onerror = () => {
      setError('Error reading the file.');
      setIsProcessing(false);
      setProgress(0);
    };
    
    reader.readAsArrayBuffer(slice);
  };

  const detectDataType = (content) => {
    if (!content || typeof content !== 'string') return 'text';
    
    const trimmed = content.trim();
    if (!trimmed) return 'text';
    
    // Check for JSON first (more strict)
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || 
        (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        JSON.parse(trimmed);
        console.log('Detected JSON content');
        return 'json';
      } catch (e) {
        console.log('JSON parse failed:', e.message);
        // Not valid JSON, continue to other checks
      }
    }
    
    // Check for XML - more comprehensive check
    if (trimmed.startsWith('<') && trimmed.includes('>')) {
      // Look for proper XML structure
      const hasClosingTag = trimmed.includes('</') || trimmed.includes('/>');
      const xmlRegex = /<[a-zA-Z][^>]*>/;
      if (xmlRegex.test(trimmed) && hasClosingTag) {
        console.log('Detected XML content');
        return 'xml';
      }
    }
    
    console.log('Detected text content');
    return 'text';
  };

  const formatContent = (content, type) => {
    if (type === 'json') {
      try {
        const parsed = JSON.parse(content);
        return JSON.stringify(parsed, null, 2);
      } catch (e) {
        return content;
      }
    }
    return content;
  };

  const generateFilenamePreview = () => {
    if (selectedNameColumns.length === 0 || data.length < 2) {
      return 'No preview available';
    }

    // Use the second row (index 1) as example
    const exampleRow = data[1];
    
    // Build filename from selected columns
    let fileName = selectedNameColumns
      .map(col => exampleRow[col] ? exampleRow[col].toString().trim() : '')
      .filter(part => part !== '')
      .join('_');
    
    // Clean filename (same logic as in extractData)
    const cleanFileName = fileName
      .replace(/[<>:"/\\|?*\x00-\x1f]/g, '_')
      .replace(/\s+/g, '_')
      .replace(/_{2,}/g, '_')
      .substring(0, 100)
      .trim()
      .replace(/^_+|_+$/g, '');
    
    // Determine file extension based on data content if available
    let fileExtension = '.txt';
    if (selectedDataColumn && exampleRow[selectedDataColumn]) {
      const content = exampleRow[selectedDataColumn].toString().trim();
      const dataType = detectDataType(content);
      if (dataType === 'json') fileExtension = '.json';
      else if (dataType === 'xml') fileExtension = '.xml';
    }
    
    return cleanFileName ? `${cleanFileName}${fileExtension}` : `file_example${fileExtension}`;
  };

  const extractData = async () => {
    if (!selectedDataColumn || selectedNameColumns.length === 0) {
      setError('Please select both data column and at least one name column.');
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setError('');

    try {
      const isCsv = file && /\.csv$/i.test(file.name);
      if (isCsv) {
        await extractDataCsv();
      } else if (isLargeFile) {
        await extractDataLarge();
      } else {
        await extractDataSmall();
      }
    } catch (err) {
      console.error('💥 Extraction error:', err);
      setError(`Error extracting data: ${err.message}`);
      setIsProcessing(false);
      setProgress(0);
    }
  };

  const extractDataSmall = async () => {
    console.log('🔍 Starting standard extraction for small file...');
    
    const extractedFiles = [];
    const validRows = data.filter(row => 
      row[selectedDataColumn] && 
      row[selectedDataColumn].toString().trim() !== '' &&
      selectedNameColumns.some(col => row[col] && row[col].toString().trim() !== '')
    );

    if (validRows.length === 0) {
      throw new Error('No valid data found in the selected columns.');
    }

    console.log(`🚀 Processing ${validRows.length} rows in chunks of ${chunkSize}`);
    
    const totalChunks = Math.ceil(validRows.length / chunkSize);
    const zipFiles = [];
    
    let fileCounter = 1;
    
    // Process data in chunks
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const startIndex = chunkIndex * chunkSize;
      const endIndex = Math.min(startIndex + chunkSize, validRows.length);
      const chunk = validRows.slice(startIndex, endIndex);
      
      console.log(`Processing chunk ${chunkIndex + 1}/${totalChunks} (${chunk.length} files)`);
      
      const zip = new JSZip();
      const chunkFiles = [];
      
      // Process each row in the current chunk
      for (let i = 0; i < chunk.length; i++) {
        const row = chunk[i];
        const content = row[selectedDataColumn].toString().trim();
        
        if (!content) continue;
        
        // Build filename from selected columns
        let fileName = selectedNameColumns
          .map(col => row[col] ? row[col].toString().trim() : '')
          .filter(part => part !== '')
          .join('_');
        
        if (!fileName) {
          fileName = `file_${fileCounter}`;
        }
        
        // Clean filename
        const cleanFileName = fileName
          .replace(/[<>:"/\\|?*\x00-\x1f]/g, '_')
          .replace(/\s+/g, '_')
          .replace(/_{2,}/g, '_')
          .substring(0, 100)
          .trim()
          .replace(/^_+|_+$/g, '');
        
        // Detect data type and format content
        const dataType = detectDataType(content);
        const formattedContent = formatContent(content, dataType);
        
        // Set proper file extension
        let fileExtension = '.txt';
        if (dataType === 'json') fileExtension = '.json';
        else if (dataType === 'xml') fileExtension = '.xml';
        
        // Ensure unique filename within this chunk
        let fullFileName = `${cleanFileName}${fileExtension}`;
        let counter = 1;
        while (chunkFiles.some(f => f.fileName === fullFileName)) {
          fullFileName = `${cleanFileName}_${counter}${fileExtension}`;
          counter++;
        }
        
        // Add to zip
        zip.file(fullFileName, formattedContent);
        
        const fileInfo = {
          fileName: fullFileName,
          type: dataType,
          size: formattedContent.length
        };
        
        chunkFiles.push(fileInfo);
        extractedFiles.push(fileInfo);

        fileCounter++;
        
        // Update progress
        const overallProgress = ((chunkIndex * chunkSize + i + 1) / validRows.length) * 90;
        setProgress(overallProgress);
      }

      if (chunkFiles.length === 0) continue;

      // Generate ZIP file for this chunk
      console.log(`Generating ZIP ${chunkIndex + 1} with ${chunkFiles.length} files...`);
      const zipContent = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });
      
      if (zipContent.size === 0) {
        console.warn(`ZIP ${chunkIndex + 1} is empty, skipping...`);
        continue;
      }
      
      // Create filename for this ZIP
      const timestamp = new Date().toISOString().split('T')[0];
      const zipFileName = totalChunks > 1 
        ? `extracted_data_${timestamp}_part${chunkIndex + 1}_of_${totalChunks}.zip`
        : `extracted_data_${timestamp}.zip`;
      
      // Save the ZIP file
      saveAs(zipContent, zipFileName);
      
      zipFiles.push({
        fileName: zipFileName,
        fileCount: chunkFiles.length,
        size: zipContent.size
      });
      
      // Small delay between ZIP generations
      if (chunkIndex < totalChunks - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    if (extractedFiles.length === 0) {
      throw new Error('No valid files could be extracted. Please check your data.');
    }

    setResults({
      totalFiles: extractedFiles.length,
      totalChunks: totalChunks,
      zipFiles: zipFiles,
      files: extractedFiles,
      jsonFiles: extractedFiles.filter(f => f.type === 'json').length,
      xmlFiles: extractedFiles.filter(f => f.type === 'xml').length,
      textFiles: extractedFiles.filter(f => f.type === 'text').length
    });

    setProgress(100);
    setIsProcessing(false);
    
    console.log(`🎉 Small file extraction complete: ${extractedFiles.length} files in ${totalChunks} ZIP archives`);
  };

  const extractDataLarge = async () => {
    console.log('🔍 Starting raw Excel extraction for large file...');
    
    // For large files, try to extract XML directly from Excel (which is a ZIP)
    const zip = new JSZip();
    const zipData = await zip.loadAsync(file);
    
    console.log('📦 Excel file loaded as ZIP, looking for worksheet data...');
    
    // Find the worksheet with the most rows
    const bestSheet = await selectBestWorksheet(zipData);
    if (!bestSheet) {
      throw new Error('No worksheet data found in Excel file');
    }

    const worksheetFile = bestSheet.file;
    console.log(`📄 Reading worksheet: ${worksheetFile}`);
    
    // Get shared strings for text lookup
    let sharedStrings = [];
    if (zipData.files['xl/sharedStrings.xml']) {
      console.log('📚 Loading shared strings...');
      sharedStrings = await loadSharedStrings(zipData);
      console.log(`📚 Loaded ${sharedStrings.length} shared strings`);
    }
    
    // Read selected worksheet XML
    const worksheetXml = bestSheet.xml || await zipData.files[worksheetFile].async('text');
    console.log(`📊 Worksheet XML loaded (${worksheetXml.length} characters)`);
    
    // Parse XML to extract data
    const parser = new DOMParser();
    const doc = bestSheet.doc || parser.parseFromString(worksheetXml, 'text/xml');
    const rows = queryTag(doc, 'row');
    
    console.log(`📊 Found ${rows.length} rows in worksheet`);
    
    if (rows.length === 0 && worksheetXml.includes('<row')) {
      console.warn('DOMParser returned 0 rows but <row> exists in XML; proceeding anyway');
    } else if (rows.length === 0) {
      throw new Error('No data rows found in worksheet');
    }
    
    // Find column indices from header row (first row)
    let actualHeaders = [];
    let useTableHeaders = tableStartColIndex !== null && Array.isArray(columns) && columns.length > 0;

    if (!useTableHeaders) {
      const headerRow = rows[0];
      const headerCells = queryTag(headerRow, 'c');
      for (let i = 0; i < headerCells.length; i++) {
        const cell = headerCells[i];
        const cellValue = getCellValue(cell, sharedStrings);
        const ref = cell.getAttribute('r');
        const colIndex = getColumnIndexFromRef(ref);
        actualHeaders[colIndex] = cellValue || `Column${colIndex + 1}`;
      }
      for (let i = 0; i < actualHeaders.length; i++) {
        if (!actualHeaders[i]) actualHeaders[i] = `Column${i + 1}`;
      }
    } else {
      actualHeaders = columns.slice(); // logical headers within table range
    }

    // Determine indices for data and name columns
    let dataColumnIndex;
    let nameColumnIndices;

    if (useTableHeaders) {
      const localDataIdx = actualHeaders.findIndex(h =>
        h && (h.toLowerCase().includes(selectedDataColumn.toLowerCase()) ||
        selectedDataColumn.toLowerCase().includes(h.toLowerCase()))
      );
      dataColumnIndex = localDataIdx !== -1 ? tableStartColIndex + localDataIdx : -1;

      nameColumnIndices = selectedNameColumns.map(selectedCol => {
        const localIdx = actualHeaders.findIndex(h =>
          h && (h.toLowerCase().includes(selectedCol.toLowerCase()) ||
          selectedCol.toLowerCase().includes(h.toLowerCase()))
        );
        return localIdx !== -1 ? tableStartColIndex + localIdx : -1;
      }).filter(idx => idx !== -1);
    } else {
      dataColumnIndex = actualHeaders.findIndex(h => 
        h && (h.toLowerCase().includes(selectedDataColumn.toLowerCase()) ||
        selectedDataColumn.toLowerCase().includes(h.toLowerCase()))
      );

      nameColumnIndices = selectedNameColumns.map(selectedCol => 
        actualHeaders.findIndex(h => 
          h && (h.toLowerCase().includes(selectedCol.toLowerCase()) ||
          selectedCol.toLowerCase().includes(h.toLowerCase()))
        )
      ).filter(idx => idx !== -1);
    }

    if (dataColumnIndex === -1) {
      console.warn(`Data column "${selectedDataColumn}" not found exactly, trying first column with data`);
    }
    
    const totalRows = rows.length - 1; // Exclude header
    const totalChunks = Math.ceil(totalRows / chunkSize);
    const zipFiles = [];
    
    let fileCounter = 1;
    let totalJsonFiles = 0;
    let totalXmlFiles = 0;
    let totalTextFiles = 0;
    
    // Process rows in chunks
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      console.log(`📦 Processing chunk ${chunkIndex + 1}/${totalChunks}`);
      
      const zipArchive = new JSZip();
      let chunkProcessedCount = 0;
      
      const startRow = chunkIndex * chunkSize + 1; // +1 to skip header
      const endRow = Math.min(startRow + chunkSize - 1, totalRows);
      
      for (let rowIndex = startRow; rowIndex <= endRow && rowIndex < rows.length; rowIndex++) {
        try {
          const row = rows[rowIndex];
          const cells = queryTag(row, 'c');

          // Build map of column index -> value based on cell reference (handles sparse rows)
          const valuesByIndex = {};
          for (let i = 0; i < cells.length; i++) {
            const colIndex = getColumnIndexFromRef(cells[i].getAttribute('r'));
            valuesByIndex[colIndex] = getCellValue(cells[i], sharedStrings);
          }

          // Extract data from target column
          let content = '';
          if (dataColumnIndex >= 0) {
            content = valuesByIndex[dataColumnIndex] || '';
          }
          if (!content) {
            // Fallback: look for any cell with substantial content
            for (const key in valuesByIndex) {
              const cellContent = valuesByIndex[key];
              if (cellContent && cellContent.length > 50) {
                content = cellContent;
                break;
              }
            }
          }

          if (!content || content.trim().length === 0) continue;

          // Extract name parts
          const nameParts = nameColumnIndices
            .map(colIndex => valuesByIndex[colIndex] || '')
            .filter(part => part && part.trim().length > 0);

          if (nameParts.length === 0) {
            nameParts.push(`file_${fileCounter}`);
          }

          // Build filename
          const fileName = nameParts.join('_')
            .replace(/[<>:"/\\|?*\x00-\x1f]/g, '_')
            .replace(/\s+/g, '_')
            .replace(/_{2,}/g, '_')
            .substring(0, 80)
            .trim()
            .replace(/^_+|_+$/g, '') || `file_${fileCounter}`;

          // Detect data type and format content
          const dataType = detectDataType(content);
          const formattedContent = formatContent(content, dataType);

          if (dataType === 'json') totalJsonFiles++;
          else if (dataType === 'xml') totalXmlFiles++;
          else totalTextFiles++;

          // Set file extension
          let fileExtension = '.txt';
          if (dataType === 'json') fileExtension = '.json';
          else if (dataType === 'xml') fileExtension = '.xml';

          const fullFileName = `${fileName}${fileExtension}`;

          // Add to zip
          zipArchive.file(fullFileName, formattedContent);

          fileCounter++;
          chunkProcessedCount++;

          // Update progress
          if (chunkProcessedCount % 50 === 0) {
            const overallProgress = Math.min(75, 5 + (chunkProcessedCount / Math.max(1, chunkSize)) * 50);
            setProgress(overallProgress);
          }

        } catch (rowError) {
          console.warn(`⚠️ Error processing row ${rowIndex}:`, rowError.message);
        }
      }

      if (chunkProcessedCount === 0) continue;

      try {
        console.log(`📂 Generating ZIP ${chunkIndex + 1} with ${chunkProcessedCount} files...`);
        
        const zipContent = await zipArchive.generateAsync({ 
          type: 'blob',
          compression: 'STORE'
        });
        
        const timestamp = new Date().toISOString().split('T')[0];
        const zipFileName = totalChunks > 1 
          ? `extracted_data_${timestamp}_part${String(chunkIndex + 1).padStart(3, '0')}_of_${totalChunks}.zip`
          : `extracted_data_${timestamp}.zip`;
        
        saveAs(zipContent, zipFileName);
        
        zipFiles.push({
          fileName: zipFileName,
          fileCount: chunkProcessedCount,
          size: zipContent.size
        });
        
        console.log(`✅ Saved ${zipFileName}: ${chunkProcessedCount} files`);
        
      } catch (zipError) {
        console.error(`❌ Error generating ZIP ${chunkIndex + 1}:`, zipError);
        throw new Error(`Failed to generate ZIP file ${chunkIndex + 1}: ${zipError.message}`);
      }
      
      // Cleanup between chunks
      await new Promise(resolve => setTimeout(resolve, 200));
      if (window.gc) window.gc();
      
      setProgress(75 + ((chunkIndex + 1) / totalChunks) * 25);
    }

    const processedCount = fileCounter - 1;

    if (processedCount === 0) {
      throw new Error('No valid files could be extracted. Please check your column selections.');
    }

    setResults({
      totalFiles: processedCount,
      totalChunks: totalChunks,
      zipFiles: zipFiles,
      files: [], // Don't store individual files for large files
      jsonFiles: totalJsonFiles,
      xmlFiles: totalXmlFiles,
      textFiles: totalTextFiles
    });

    setProgress(100);
    setIsProcessing(false);
    
    console.log(`🎉 Large file extraction complete: ${processedCount} files in ${totalChunks} ZIP archives`);
  };

  const extractDataCsv = async () => {
    console.log('🔍 Starting CSV streaming extraction...');

    return new Promise((resolve, reject) => {
      const zipFiles = [];
      let fileCounter = 1;
      let chunkProcessedCount = 0;
      let totalFiles = 0;

      let zipArchive = new JSZip();

      const flushZip = async (isFinal = false) => {
        if (chunkProcessedCount === 0) return;
        try {
          const timestamp = new Date().toISOString().split('T')[0];
          const partIndex = zipFiles.length + 1;
          const zipFileName = `${isFinal && partIndex === 1 ? 'extracted_data' : `extracted_data_part${String(partIndex).padStart(3, '0')}`}_${timestamp}.zip`;
          const zipContent = await zipArchive.generateAsync({ type: 'blob', compression: 'STORE' });
          saveAs(zipContent, zipFileName);
          zipFiles.push({ fileName: zipFileName, fileCount: chunkProcessedCount, size: zipContent.size });
          console.log(`✅ Saved ${zipFileName}: ${chunkProcessedCount} files`);
        } catch (e) {
          reject(e);
          return;
        }
        // Reset for next chunk
        zipArchive = new JSZip();
        chunkProcessedCount = 0;
        if (!isFinal) {
          await new Promise(r => setTimeout(r, 100));
        }
      };

      Papa.parse(file, {
        header: true,
        worker: true,
        skipEmptyLines: true,
        chunkSize: 1024 * 1024, // 1MB chunks
        chunk: async (results, parser) => {
          try {
            const rows = results.data;
            for (let i = 0; i < rows.length; i++) {
              const row = rows[i];
              const raw = row[selectedDataColumn];
              const content = raw != null ? String(raw).trim() : '';
              if (!content) continue;

              const nameParts = selectedNameColumns
                .map(col => (row[col] != null ? String(row[col]).trim() : ''))
                .filter(Boolean);
              const base = nameParts.length > 0 ? nameParts.join('_') : `file_${fileCounter}`;
              const cleanFileName = base
                .replace(/[<>:"/\\|?*\x00-\x1f]/g, '_')
                .replace(/\s+/g, '_')
                .replace(/_{2,}/g, '_')
                .substring(0, 80)
                .trim()
                .replace(/^_+|_+$/g, '') || `file_${fileCounter}`;

              const dataType = detectDataType(content);
              const formattedContent = formatContent(content, dataType);
              let fileExtension = '.txt';
              if (dataType === 'json') fileExtension = '.json';
              else if (dataType === 'xml') fileExtension = '.xml';

              const fullFileName = `${cleanFileName}${fileExtension}`;
              zipArchive.file(fullFileName, formattedContent);

              fileCounter++;
              chunkProcessedCount++;
              totalFiles++;
            }

            if (chunkProcessedCount >= chunkSize) {
              parser.pause();
              await flushZip(false);
              setProgress(Math.min(95, (totalFiles % (chunkSize * 10)) / (chunkSize * 10) * 95));
              parser.resume();
            }
          } catch (err) {
            parser.abort();
            reject(err);
          }
        },
        complete: async () => {
          try {
            await flushZip(true);

            setResults({
              totalFiles: totalFiles,
              totalChunks: zipFiles.length,
              zipFiles: zipFiles,
              files: [],
              jsonFiles: 0,
              xmlFiles: 0,
              textFiles: 0
            });

            setProgress(100);
            setIsProcessing(false);
            console.log(`🎉 CSV extraction complete: ${totalFiles} files in ${zipFiles.length} ZIP archives`);
            resolve();
          } catch (e) {
            reject(e);
          }
        },
        error: (err) => {
          setIsProcessing(false);
          reject(err);
        }
      });
    });
  };

  // Namespace-agnostic tag query helper
  const queryTag = (node, tag) => {
    if (!node) return [];
    let nodes = [];
    if (typeof node.getElementsByTagName === 'function') {
      nodes = node.getElementsByTagName(tag);
      if (nodes && nodes.length > 0) return nodes;
    }
    if (typeof node.getElementsByTagNameNS === 'function') {
      try {
        const nsNodes = node.getElementsByTagNameNS('*', tag);
        if (nsNodes && nsNodes.length > 0) return nsNodes;
      } catch (_) {}
    }
    return nodes;
  };

  // Helper function to extract cell values from Excel XML
  const getCellValue = (cell, sharedStrings) => {
    const vEls = queryTag(cell, 'v');
    const valueElement = vEls && vEls[0];
    if (!valueElement) return '';
    
    const cellType = cell.getAttribute('t');
    const value = valueElement.textContent;
    
    if (cellType === 's') {
      // Shared string reference
      const index = parseInt(value);
      return sharedStrings[index] || '';
    } else if (cellType === 'inlineStr') {
      const isEls = queryTag(cell, 'is');
      const tEls = isEls && isEls[0] ? queryTag(isEls[0], 't') : [];
      return tEls && tEls[0] ? tEls[0].textContent : '';
    } else {
      // Direct value
      return value;
    }
  };

  // Convert a cell reference like "C12" to a zero-based column index
  const getColumnIndexFromRef = (ref) => {
    if (!ref) return 0;
    // Extract letters from the ref
    const match = /^[A-Za-z]+/.exec(ref);
    const letters = match ? match[0].toUpperCase() : 'A';
    let index = 0;
    for (let i = 0; i < letters.length; i++) {
      index = index * 26 + (letters.charCodeAt(i) - 64); // 'A' => 1
    }
    return index - 1; // zero-based
  };

  // Load shared strings as an array where index maps to full string; supports rich text runs
  const loadSharedStrings = async (zipData) => {
    if (!zipData.files['xl/sharedStrings.xml']) return [];
    const xml = await zipData.files['xl/sharedStrings.xml'].async('text');
    const doc = new DOMParser().parseFromString(xml, 'application/xml');
    const siEls = queryTag(doc, 'si');
    const values = [];
    for (let i = 0; i < siEls.length; i++) {
      const si = siEls[i];
      const tNodes = queryTag(si, 't');
      if (!tNodes || tNodes.length === 0) {
        values.push('');
      } else if (tNodes.length === 1) {
        values.push(tNodes[0].textContent || '');
      } else {
        let combined = '';
        for (let j = 0; j < tNodes.length; j++) {
          combined += tNodes[j].textContent || '';
        }
        values.push(combined);
      }
    }
    return values;
  };

  // Select the worksheet with the most rows (fallback if first sheet is empty/metadata)
  const selectBestWorksheet = async (zipData) => {
    const worksheetFiles = Object.keys(zipData.files).filter(name =>
      name.startsWith('xl/worksheets/') && name.endsWith('.xml')
    );
    if (worksheetFiles.length === 0) return null;

    let best = { file: worksheetFiles[0], rows: 0, doc: null, xml: '' };

    for (const file of worksheetFiles) {
      try {
        const xml = await zipData.files[file].async('text');
        const doc = new DOMParser().parseFromString(xml, 'application/xml');

        // Prefer dimension-based estimate to avoid querying all rows
        let rowCount = 0;
        const dimEls = queryTag(doc, 'dimension');
        const dim = dimEls && dimEls[0];
        if (dim && dim.getAttribute) {
          const ref = dim.getAttribute('ref'); // e.g., A1:K250001
          const m = ref ? /(\w+)(\d+):(\w+)(\d+)/.exec(ref) : null;
          if (m) {
            const r1 = parseInt(m[2], 10);
            const r2 = parseInt(m[4], 10);
            if (!Number.isNaN(r1) && !Number.isNaN(r2)) {
              rowCount = Math.max(rowCount, Math.abs(r2 - r1) + 1);
            }
          }
        }
        if (rowCount === 0) {
          // Fallback to counting actual row elements
          rowCount = queryTag(doc, 'row').length;
        }

        if (rowCount > best.rows) {
          best = { file, rows: rowCount, doc, xml };
        }
      } catch (_) {
        // ignore parsing errors and continue
      }
    }

    return best.rows > 0 ? best : { file: worksheetFiles[0], rows: 0, doc: null, xml: await zipData.files[worksheetFiles[0]].async('text') };
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const resetApp = () => {
    setFile(null);
    setData([]);
    setColumns([]);
    setSelectedDataColumn('');
    setSelectedNameColumns([]);
    setResults(null);
    setError('');
    setProgress(0);
    setChunkSize(1000);
    setManualMode(false);
    setManualColumns('');
    setIsLargeFile(false);
    setFileSizeInfo('');
    setTableStartColIndex(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="container">
      <div className="card">
        <div className="header">
          <h1>📊 Excel JSON/XML Extractor</h1>
          <p>Upload an Excel file and extract JSON or XML data from any column</p>
        </div>

        <div className="info-section">
          <div className="info-card">
            <h3>🔒 Privacy First</h3>
            <p>All processing happens locally in your browser. Your files never leave your device or get uploaded to any server.</p>
          </div>
          <div className="info-card">
            <h3>⚡ How It Works</h3>
            <p>1. Upload your Excel file (.xlsx, .xls, .csv)<br/>
               2. Select the column containing JSON/XML data<br/>
               3. Choose columns for file naming<br/>
               4. Download extracted files as a ZIP archive</p>
          </div>
          <div className="info-card">
            <h3>🎯 Key Features</h3>
            <p>• Automatic JSON/XML detection<br/>
               • Multi-column filename creation<br/>
               • Proper file extensions (.json, .xml)<br/>
               • Batch processing with progress tracking</p>
          </div>
        </div>

        {error && (
          <div className="error">
            <strong>Error:</strong> {error}
          </div>
        )}

        {!file ? (
          <div
            className={`upload-area ${dragOver ? 'dragover' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="upload-icon">📁</div>
            <div className="upload-text">Click to select or drag & drop an Excel file</div>
            <div className="upload-subtext">Supports .xlsx, .xls, and .csv files</div>
            <input
              ref={fileInputRef}
              type="file"
              className="file-input"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => handleFileSelect(e.target.files[0])}
            />
          </div>
        ) : (
          <div>
            <div className="file-info">
              <h3>📄 File Loaded: {file.name}</h3>
              <p>{fileSizeInfo} | {isLargeFile ? 'Memory-Optimized Mode' : `${data.length} rows loaded`} | {columns.length} columns</p>
              
              {isLargeFile ? (
                <div style={{ 
                  marginTop: '0.5rem', 
                  padding: '0.75rem', 
                  background: '#f0f9ff', 
                  border: '1px solid #0ea5e9', 
                  borderRadius: '6px',
                  fontSize: '0.85rem'
                }}>
                  <div style={{ color: '#0369a1', fontWeight: '500', marginBottom: '0.25rem' }}>
                    🚀 Large File Mode Active (&gt;10MB)
                  </div>
                  <div style={{ color: '#0369a1' }}>
                    • Memory-optimized raw XML processing
                    <br/>• Column structure detected from headers
                    <br/>• Full data processed during extraction only
                  </div>
                </div>
              ) : (
                <div style={{ 
                  marginTop: '0.5rem', 
                  padding: '0.75rem', 
                  background: '#f0fdf4', 
                  border: '1px solid #22c55e', 
                  borderRadius: '6px',
                  fontSize: '0.85rem'
                }}>
                  <div style={{ color: '#15803d', fontWeight: '500', marginBottom: '0.25rem' }}>
                    ✨ Full Featured Mode (≤10MB)
                  </div>
                  <div style={{ color: '#15803d' }}>
                    • Complete data preview available
                    <br/>• Visual filename preview
                    <br/>• All rows loaded for immediate processing
                  </div>
                </div>
              )}
            </div>

            {error && error.includes('File too large for structure detection') && (
              <div style={{ marginTop: '1rem' }}>
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    setManualMode(true);
                    setError('');
                  }}
                  style={{ marginRight: '1rem' }}
                >
                  📝 Enter Column Names Manually
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    setError('');
                    // Use the fallback generic columns
                  }}
                >
                  ✅ Use Generic Columns
                </button>
              </div>
            )}

            {manualMode && (
              <div style={{ marginTop: '1rem', padding: '1rem', border: '2px solid #3b82f6', borderRadius: '8px', background: '#f8fafc' }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#1e40af' }}>📝 Manual Column Setup</h4>
                <div className="form-group">
                  <label className="form-label">Enter Column Names (comma-separated):</label>
                  <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                    Example: ID, Name, Description, JsonData, Category
                  </p>
                  <textarea
                    className="form-select"
                    value={manualColumns}
                    onChange={(e) => setManualColumns(e.target.value)}
                    placeholder="Column1, Column2, Column3, JsonData, ..."
                    rows="3"
                    style={{ width: '100%', resize: 'vertical' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                  <button 
                    className="btn"
                    onClick={() => {
                      const cols = manualColumns.split(',').map(c => c.trim()).filter(c => c);
                      if (cols.length > 0) {
                        setColumns(cols);
                        const sampleRow = {};
                        cols.forEach(col => {
                          sampleRow[col] = 'Manual column - data will be read during processing';
                        });
                        setData([sampleRow]);
                        setManualMode(false);
                        console.log(`📝 Manual columns set: ${cols.join(', ')}`);
                      }
                    }}
                    disabled={!manualColumns.trim()}
                  >
                    ✅ Set Columns
                  </button>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => {
                      setManualMode(false);
                      setManualColumns('');
                    }}
                  >
                    ❌ Cancel
                  </button>
                </div>
              </div>
            )}

            {isProcessing && !results && (
              <div className="loading">
                <div className="spinner"></div>
                <p>Processing file... {Math.round(progress)}%</p>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                </div>
              </div>
            )}

            {data.length > 0 && !isProcessing && (
              <div>
                <div className="form-group">
                  <label className="form-label">Files per ZIP Archive:</label>
                  <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                    For large datasets, files will be split into multiple ZIP archives. Lower values use less memory.
                  </p>
                  <input
                    type="number"
                    className="form-select"
                    value={chunkSize}
                    onChange={(e) => setChunkSize(Math.max(1, parseInt(e.target.value) || 5000))}
                    min="1"
                    max="10000"
                    style={{ width: '200px' }}
                  />
                  <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Recommended: 1000-5000 for most files, 500-1000 for very large files
                  </p>
                  {selectedDataColumn && data.length > 0 && (
                    <div style={{ 
                      marginTop: '0.5rem', 
                      padding: '0.5rem', 
                      background: '#f0f9ff', 
                      border: '1px solid #bae6fd', 
                      borderRadius: '6px',
                      fontSize: '0.8rem'
                    }}>
                      <div style={{ color: '#0369a1', marginBottom: '0.25rem' }}>
                        📊 Processing Estimates:
                      </div>
                      {isLargeFile ? (
                        <>
                          <div style={{ color: '#1e40af' }}>
                            • Files per ZIP: {chunkSize}
                          </div>
                          <div style={{ color: '#1e40af' }}>
                            • Memory usage: {chunkSize > 7500 ? 'High' : chunkSize > 2500 ? 'Medium' : 'Low'}
                          </div>
                          <div style={{ color: '#1e40af' }}>
                            • Total rows will be determined during processing
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ color: '#1e40af' }}>
                            • Valid rows: ~{data.filter(row => 
                              row[selectedDataColumn] && 
                              row[selectedDataColumn].toString().trim() !== ''
                            ).length} files
                          </div>
                          <div style={{ color: '#1e40af' }}>
                            • ZIP archives: ~{Math.ceil(data.filter(row => 
                              row[selectedDataColumn] && 
                              row[selectedDataColumn].toString().trim() !== ''
                            ).length / chunkSize)} files
                          </div>
                          <div style={{ color: '#1e40af' }}>
                            • Memory usage: {chunkSize > 7500 ? 'High' : chunkSize > 2500 ? 'Medium' : 'Low'}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Select Data Column (contains JSON/XML):</label>
                  <select
                    className="form-select"
                    value={selectedDataColumn}
                    onChange={(e) => setSelectedDataColumn(e.target.value)}
                  >
                    <option value="">-- Choose Column --</option>
                    {columns.map((column, index) => (
                      <option key={index} value={column}>
                        {column}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Select Name Columns (for file names):</label>
                  <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                    Select one or more columns to create filenames (will be joined with underscores)
                  </p>
                  <div style={{ border: '2px solid #d1d5db', borderRadius: '8px', padding: '0.5rem', maxHeight: '120px', overflowY: 'auto' }}>
                    {columns.map((column, index) => (
                      <label key={index} style={{ display: 'block', padding: '0.25rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={selectedNameColumns.includes(column)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedNameColumns([...selectedNameColumns, column]);
                            } else {
                              setSelectedNameColumns(selectedNameColumns.filter(col => col !== column));
                            }
                          }}
                          style={{ marginRight: '0.5rem' }}
                        />
                        {column}
                      </label>
                    ))}
                  </div>
                  {selectedNameColumns.length > 0 && (
                    <div style={{ marginTop: '0.75rem' }}>
                      <p style={{ fontSize: '0.8rem', color: '#10b981', marginBottom: '0.5rem' }}>
                        Selected: {selectedNameColumns.join(', ')}
                      </p>
                      {data.length > 1 && (
                        <div style={{ 
                          background: '#f0f9ff', 
                          border: '1px solid #bae6fd', 
                          borderRadius: '6px', 
                          padding: '0.75rem',
                          fontSize: '0.85rem'
                        }}>
                          <div style={{ color: '#0369a1', fontWeight: '500', marginBottom: '0.25rem' }}>
                            📄 Filename Preview (using row 2 as example):
                          </div>
                          <code style={{ 
                            background: '#ffffff', 
                            padding: '0.25rem 0.5rem', 
                            borderRadius: '4px',
                            color: '#1e40af',
                            fontFamily: 'monospace',
                            fontSize: '0.8rem'
                          }}>
                            {generateFilenamePreview()}
                          </code>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {selectedDataColumn && selectedNameColumns.length > 0 && (
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                    <button
                      className="btn"
                      onClick={extractData}
                      disabled={isProcessing}
                    >
                      {isProcessing ? 'Extracting...' : '🚀 Extract Data'}
                    </button>
                    <button className="btn btn-secondary" onClick={resetApp}>
                      📤 Upload New File
                    </button>
                  </div>
                )}
              </div>
            )}

            {results && (
              <div className="result-summary">
                <h3>✅ Extraction Complete!</h3>
                <p><strong>Total files extracted:</strong> {results.totalFiles}</p>
                {results.totalChunks > 1 && (
                  <p><strong>ZIP archives created:</strong> {results.totalChunks}</p>
                )}
                <p><strong>JSON files:</strong> {results.jsonFiles}</p>
                <p><strong>XML files:</strong> {results.xmlFiles}</p>
                <p><strong>Text files:</strong> {results.textFiles}</p>
                <p style={{ marginTop: '1rem', fontSize: '0.9rem', opacity: 0.8 }}>
                  {results.totalChunks > 1 
                    ? `Files have been split into ${results.totalChunks} ZIP archives and downloaded automatically.`
                    : 'Files have been packaged into a ZIP file and downloaded automatically.'
                  }
                </p>
                
                {results.zipFiles && results.zipFiles.length > 0 && (
                  <details style={{ marginTop: '1rem', fontSize: '0.8rem' }}>
                    <summary style={{ cursor: 'pointer', color: '#6b7280' }}>
                      View ZIP archive details
                    </summary>
                    <div style={{ marginTop: '0.5rem', maxHeight: '150px', overflow: 'auto' }}>
                      {results.zipFiles.map((zipFile, index) => (
                        <div key={index} style={{ 
                          padding: '4px 8px', 
                          fontSize: '0.75rem',
                          background: index % 2 === 0 ? '#f9fafb' : 'transparent',
                          borderRadius: '3px'
                        }}>
                          <strong>{zipFile.fileName}</strong><br/>
                          {zipFile.fileCount} files, {(zipFile.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                
                {results.files.length > 0 ? (
                  <details style={{ marginTop: '1rem', fontSize: '0.8rem' }}>
                    <summary style={{ cursor: 'pointer', color: '#6b7280' }}>
                      View extracted files (first 10)
                    </summary>
                    <div style={{ marginTop: '0.5rem', maxHeight: '150px', overflow: 'auto' }}>
                      {results.files.slice(0, 10).map((file, index) => (
                        <div key={index} style={{ padding: '2px 0', fontSize: '0.75rem' }}>
                          {file.fileName} ({file.type}, {file.size} bytes)
                        </div>
                      ))}
                      {results.files.length > 10 && (
                        <div style={{ padding: '2px 0', fontSize: '0.75rem', fontStyle: 'italic' }}>
                          ... and {results.files.length - 10} more files
                        </div>
                      )}
                    </div>
                  </details>
                ) : (
                  <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#6b7280', fontStyle: 'italic' }}>
                    Individual file details not shown for large datasets (memory optimization)
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default App; 